import { useMemo } from 'react'
import type { FeatureRolloutState } from '@/client/types.gen'
import { useAuth } from '@/context'
import type { FeatureRolloutKey } from '@/types/featureRollout'

export interface UseFeatureRolloutResult {
	key: FeatureRolloutKey
	name: string
	description: string
	enabled: boolean
	phase: FeatureRolloutState['phase'] | undefined
	optIn: boolean
	canSelfOptIn: boolean
	canSelfOptOut: boolean
	userOptInExposed: boolean
	state: FeatureRolloutState | undefined
	isLoading: boolean
}

export function useFeatureRollout(key: FeatureRolloutKey): UseFeatureRolloutResult {
	const { user, loading } = useAuth()
	const state = user?.feature_rollouts?.[key]

	return useMemo(
		() => ({
			key,
			name: state?.name ?? key,
			description: state?.description ?? '',
			enabled: state?.enabled ?? false,
			phase: state?.phase,
			optIn: state?.opt_in ?? false,
			canSelfOptIn: state?.can_self_opt_in ?? false,
			canSelfOptOut: state?.can_self_opt_out ?? false,
			userOptInExposed: state?.user_opt_in_exposed ?? false,
			state,
			isLoading: loading,
		}),
		[key, state, loading]
	)
}

export function useExposedBetaFeatureRollouts(): FeatureRolloutState[] {
	const { user } = useAuth()
	const rollouts = user?.feature_rollouts
	if (!rollouts) return []

	const entries = Object.values(rollouts)
	const listedKeys = new Set(
		entries
			.filter(
				(entry) => entry.user_opt_in_exposed && (entry.can_self_opt_in || entry.can_self_opt_out)
			)
			.map((entry) => entry.key)
	)
	for (const entry of entries) {
		if (!listedKeys.has(entry.key)) continue
		for (const requiredKey of entry.requires ?? []) {
			const required = rollouts[requiredKey]
			if (required?.user_opt_in_exposed) {
				listedKeys.add(requiredKey)
			}
		}
	}

	return entries.filter((entry) => listedKeys.has(entry.key))
}
